import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Course } from '@core/models/course.model';
import { PaginatedApiResponse } from '@core/models/common/paginated-api-response.model';
import { ApiResponse } from '@core/models/common/api-response.model';
import { ApiResponseHandler } from '@core/handlers/api-response.handler';
import { CourseChapter } from '@core/models/course-chapter.model';
import { QuestionSet } from '@core/models/question-set.model';
import { Lesson } from '@core/models/lesson.model';
import { ParticipantType } from '@core/models/participant-type.model';
import { Category } from '@core/models/category.model';
import { PracticeAid } from '@core/models/practice-aid.model';
import { APP_CONFIG } from '@core/config/app-config.token';
import { AppConfig } from '@core/config/app-config.model';

@Injectable({
  providedIn: 'root',
})
export class CourseService {
  private courseUrl!: string;
  private participantTypeUrl!: string;
  private categoryUrl!: string;

  constructor(
    private readonly http: HttpClient,
    @Inject(APP_CONFIG) private readonly cfg: AppConfig
  ) {
    this.courseUrl = `${this.cfg.serviceApiUrl}/courses`;
    this.participantTypeUrl = `${this.cfg.serviceApiUrl}/participant-types`;
    this.categoryUrl = `${this.cfg.serviceApiUrl}/categories`;
  }

  getCourses(page: number = 1): Observable<PaginatedApiResponse<Course>> {
    const params = { page: page.toString() };

    return this.http
      .get<PaginatedApiResponse<Course>>(this.courseUrl, { params })
      .pipe(catchError(ApiResponseHandler.handleError));
  }

  getCoursesWithParams(params: Record<string, string>): Observable<PaginatedApiResponse<Course>> {
    return this.http
      .get<PaginatedApiResponse<Course>>(this.courseUrl, { params })
      .pipe(catchError(ApiResponseHandler.handleError));
  }

  getFeaturedCourses(): Observable<Course[]> {
    const params = new HttpParams().set('is_featured', '1');
    return this.http.get<PaginatedApiResponse<Course>>(this.courseUrl, { params }).pipe(
      map((response) => ApiResponseHandler.extractData<Course>(response) as Course[]),
      catchError(ApiResponseHandler.handleError)
    );
  }

  getCourse(uuid: string): Observable<Course> {
    return this.http.get<ApiResponse<Course>>(`${this.courseUrl}/${uuid}`).pipe(
      map((response) => ApiResponseHandler.extractData<Course>(response) as Course),
      catchError(ApiResponseHandler.handleError)
    );
  }

  getCourseChapters(courseUuid: string): Observable<CourseChapter> {
    return this.http
      .get<ApiResponse<CourseChapter>>(`${this.courseUrl}/${courseUuid}/chapters`)
      .pipe(
        map((response) => ApiResponseHandler.extractData<CourseChapter>(response) as CourseChapter),
        catchError(ApiResponseHandler.handleError)
      );
  }

  getQuestionSet(courseUuid: string, questionSetUuid: string): Observable<QuestionSet> {
    return this.http
      .get<
        ApiResponse<QuestionSet>
      >(`${this.courseUrl}/${courseUuid}/questionSets/${questionSetUuid}`)
      .pipe(
        map((response) => ApiResponseHandler.extractData<QuestionSet>(response) as QuestionSet),
        catchError(ApiResponseHandler.handleError)
      );
  }

  getLesson(courseUuid: string, lessonUuid: string): Observable<Lesson> {
    return this.http
      .get<ApiResponse<Lesson>>(`${this.courseUrl}/${courseUuid}/lessons/${lessonUuid}`)
      .pipe(
        map((response) => ApiResponseHandler.extractData<Lesson>(response) as Lesson),
        catchError(ApiResponseHandler.handleError)
      );
  }

  getParticipantTypes(): Observable<ParticipantType[]> {
    return this.http.get<PaginatedApiResponse<ParticipantType>>(this.participantTypeUrl).pipe(
      map(
        (response) => ApiResponseHandler.extractData<ParticipantType>(response) as ParticipantType[]
      ),
      catchError(ApiResponseHandler.handleError)
    );
  }

  getCategories(): Observable<Category[]> {
    return this.http.get<PaginatedApiResponse<Category>>(this.categoryUrl).pipe(
      map((response) => ApiResponseHandler.extractData<Category>(response) as Category[]),
      catchError(ApiResponseHandler.handleError)
    );
  }

  getCoursePracticeAids(courseUuid: string): Observable<PracticeAid[]> {
    return this.http
      .get<ApiResponse<PracticeAid[]>>(`${this.courseUrl}/${courseUuid}/practice-aids`)
      .pipe(
        map((response) => ApiResponseHandler.extractData<PracticeAid[]>(response) as PracticeAid[]),
        catchError(ApiResponseHandler.handleError)
      );
  }

  getCourseSlideAids(courseUuid: string): Observable<PracticeAid[]> {
    return this.http
      .get<ApiResponse<PracticeAid[]>>(`${this.courseUrl}/${courseUuid}/slide-media`)
      .pipe(
        map((response) => ApiResponseHandler.extractData<PracticeAid[]>(response) as PracticeAid[]),
        catchError(ApiResponseHandler.handleError)
      );
  }

  getPracticeAidDownloadUrl(uuid: string): Observable<string> {
    return this.http.get<{ data: string }>(`${this.cfg.serviceApiUrl}/practice-aid/${uuid}`).pipe(
      map((response) => response.data),
      catchError(ApiResponseHandler.handleError)
    );
  }

  getRelatedCourses(courseUuid: string): Observable<Course[]> {
    return this.http.get<ApiResponse<Course[]>>(`${this.courseUrl}/${courseUuid}/related`).pipe(
      map((response) => ApiResponseHandler.extractData<Course[]>(response) as Course[]),
      catchError(ApiResponseHandler.handleError)
    );
  }
}
